import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '@@common';

import { CreateNewIntegrationEventModel, IntegrationEventsClient, UpdateIntegrationEventModel } from '../../../api/api';

export const integrationEventQueryKey = (contextId?: string) => ['integrationEvents', contextId ?? 'all'] as const;

export const useIntegrationEventsController = (contextId?: string) => {
	const client = useApiClient(IntegrationEventsClient);
	const queryClient = useQueryClient();

	const eventsQuery = useQuery({
		queryKey: integrationEventQueryKey(contextId),
		queryFn: () => client.search(contextId, undefined),
		enabled: contextId !== '',
		staleTime: 30000,
	});

	const createMutation = useMutation({
		mutationFn: ({ name, description, targetContextId }: { name: string; description: string; targetContextId: string }) =>
			client.create({ name, description, contextId: targetContextId, tags: [] } as CreateNewIntegrationEventModel),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationEventQueryKey(contextId) }),
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, update }: { id: string; update: UpdateIntegrationEventModel }) => client.updateById(id, update),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationEventQueryKey(contextId) }),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => client.deleteById(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationEventQueryKey(contextId) }),
	});

	return {
		integrationEvents: eventsQuery.data ?? [],
		loadingIntegrationEvents: eventsQuery.isLoading,
		createIntegrationEvent: (name: string, description: string, targetContextId: string) =>
			createMutation.mutateAsync({ name, description, targetContextId }),
		updateIntegrationEvent: (id: string, update: UpdateIntegrationEventModel) => updateMutation.mutateAsync({ id, update }),
		deleteIntegrationEvent: (id: string) => deleteMutation.mutateAsync(id),
		reloadIntegrationEvents: async () => {
			await eventsQuery.refetch();
		},
	};
};
